/**
 * AI 匹配解读（demo 改造①）：点击后调 POST /match/summary，把后端 MatchResult 交给 LLM
 * 生成一段自然语言总结。via 字段据实标注：'llm' = 大模型生成；'rule' = 规则模板（未配 LLM Key 或调用失败）。
 * 前端不传任何前端计算的分数，candidate 原样回传后端返回的 MatchResult。
 */

import { useMutation } from '@tanstack/react-query';
import type { MatchResult } from '@crossmatch/shared';

import { matchApi } from '../api/match.js';
import type { MatchSummaryPayload } from '../api/types.js';
import { toUserMessage } from '../api/errors.js';
import { Button } from './Button.js';
import { Tag } from './Tag.js';

interface AiSummaryBlockProps {
  result: MatchResult;
  /** 本次匹配的需求上下文（原样回传后端，前端不做加工） */
  requiredSkills: string[];
  weeklyHoursNeeded?: number;
}

export function AiSummaryBlock({ result, requiredSkills, weeklyHoursNeeded }: AiSummaryBlockProps) {
  const mutation = useMutation({
    mutationFn: (payload: MatchSummaryPayload) => matchApi.summarize(payload),
  });

  if (!mutation.isSuccess) {
    return (
      <div className="ai-summary">
        <Button
          variant="secondary"
          size="sm"
          loading={mutation.isPending}
          onClick={() => mutation.mutate({ candidate: result, requiredSkills, weeklyHoursNeeded })}
        >
          {mutation.isPending ? 'AI 解读生成中…' : '✨ 生成 AI 解读'}
        </Button>
        {mutation.isError ? (
          <span className="field-hint">解读失败:{toUserMessage(mutation.error)}（不影响上方分数与理由）</span>
        ) : (
          <span className="field-hint">由大模型基于上方匹配结果生成；未配 Key 时自动回退规则解读。</span>
        )}
      </div>
    );
  }

  return (
    <div className="ai-summary ai-summary-done">
      <div className="ai-summary-head">
        <span className="ai-summary-spark" aria-hidden="true">
          ✨
        </span>
        <span className="card-sub">AI 匹配解读</span>
        {mutation.data.via === 'llm' ? (
          <Tag tone="primary" title="由后端真实调用大模型生成">
            LLM 生成
          </Tag>
        ) : (
          <Tag tone="muted" title="未配置 LLM Key 或调用失败，由规则模板生成">
            规则解读
          </Tag>
        )}
        <button type="button" className="ai-summary-retry" onClick={() => mutation.reset()}>
          重新生成
        </button>
      </div>
      <p className="ai-summary-text">{mutation.data.summary}</p>
    </div>
  );
}
